import {
  AfterViewInit, Component, ElementRef, HostListener, inject, Input, OnChanges,
  OnDestroy, SimpleChanges, ViewChild
} from '@angular/core';
import {
  BarController, BarElement, CategoryScale, Chart, Legend, LinearScale,
  Tooltip
} from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface StackedBarDataset {
  label: string;
  data: number[];
  color?: string;
}

export interface StackedBarChartData {
  labels: string[];
  datasets: StackedBarDataset[];
}

@Component({
  selector: 'urban-stacked-barchart',
  templateUrl: './stacked-barchart.component.html',
  styleUrls: ['./stacked-barchart.component.scss'],
  standalone: false,
})
export class StackedBarchartComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  @Input() public chartData: StackedBarChartData;
  @Input() public limitValue: number;
  @Input() public darkThemeActive: boolean;
  @Input() public passedHeight: number = 260;
  @Input() public legendPosition: 'top' | 'bottom' = 'top';
  @Input() public horizontal: boolean = false;

  @ViewChild('chartCanvas', { static: true })
  private chartCanvas: ElementRef<HTMLCanvasElement>;

  private readonly translate = inject(TranslateService);

  private chart: Chart;
  public noDataOnChart: boolean = false;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public constructor() {
    Chart.register(
      BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend
    );
  }

  @HostListener('window:resize')
  public onResize(): void {
    if (this.chart) {
      this.chart.resize();
    }
  }

  public ngAfterViewInit(): void {
    this.draw();
    this.translate.onLangChange
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.draw());
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    this.draw();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.destroyChart();
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private draw(): void {
    this.destroyChart();
    const data: StackedBarChartData = this.chartData;
    const hasData: boolean = !!data
      && data.labels?.length > 0
      && data.datasets?.length > 0;
    this.noDataOnChart = !hasData;
    if (!hasData) {
      return;
    }

    const textColor: string = this.darkThemeActive ? '#F3F9FF' : '#151515';
    const grid: string = this.darkThemeActive
      ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

    const palette: string[] = this.getPalette();
    const datasets: any[] = data.datasets.map((d, i) => ({
      label: d.label,
      data: d.data,
      backgroundColor: d.color ?? palette[i % palette.length],
      borderColor: 'transparent',
      stack: 's'
    }));

    const ctx: CanvasRenderingContext2D =
      this.chartCanvas.nativeElement.getContext('2d');

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: { labels: data.labels, datasets },
      options: {
        indexAxis: this.horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { stacked: true, ticks: { color: textColor },
            grid: { color: grid } },
          y: { stacked: true, ticks: { color: textColor },
            grid: { color: grid }, beginAtZero: true,
            suggestedMax: this.limitValue > 0 ? this.limitValue : undefined }
        },
        plugins: {
          legend: { position: this.legendPosition,
            labels: { color: textColor } },
          tooltip: { mode: 'index', intersect: false }
        }
      }
    });
  }

  private getPalette(): string[] {
    const vars: string[] = [
      '--custom-light-primary-main-color',
      '--custom-chart-yellow-color',
      '--custom-chart-red-color',
      '--custom-chart-green-color',
      '--custom-chart-1-color',
      '--custom-chart-2-color',
      '--custom-chart-3-color',
      '--custom-chart-4-color',
    ];
    return vars.map(v => this.cssVar(v, '#0971CE'));
  }

  private cssVar(name: string, fallback: string): string {
    const v: string = window.getComputedStyle(document.body)
      .getPropertyValue(name).trim();
    return v || fallback;
  }
}
